<template>
  <div :style="companyDynamicStyle">
    <div
      class="mw-header-container container-fluid"
      :class="{ 'expand-header': expandHeader }"
    >
      <div class="row align-items-center justify-content-between mobile-navbar">
        <div class="col-auto px-0 d-flex align-items-center header-row">
          <div
            @click="
              goToWebTranslatedEntity({
                entityType: 'Knowledge',
                query: { source: 'navigation' },
              })
            "
            class="mx-header-logo-wrapper d-flex align-items-center ml-2"
          >
            <img :src="companyLogo" alt="company_logo" class="mw-header-logo" />
          </div>
          <h1 class="mb-0 ml-2 help-center">
            <TextEllipsis :text="mainHeader" :fontSize="'26px'" />
          </h1>
          <TranslationDropdown
            class="ml-2"
            v-if="focusKnowledgeIsMultilinguale"
            :current-language="navigationLanguage"
            :languages="focusKnowledgeLanguages"
            :tight="true"
            @change-language="changeLanguage"
          />
        </div>
        <div class="col-auto d-flex align-items-center">
          <NotificationPopover class="mr-3" />
          <MaydayWebUserFavorites class="mr-3" />
          <MaydayWebUserAvatar @open-feedback="openFeedbackModal" />
        </div>
      </div>

      <UrgentNotificationsCard
        v-if="isHome && !loading"
        class="urgent-notifications-card"
        :notifications="urgentNotifications"
      />

      <div class="row">
        <div
          class="col-11 col-lg-6 mw-search-wrapper"
          :class="{ 'is-home': isHome }"
        >
          <MaydayWebSearchWrapper />
        </div>
      </div>
    </div>
    <div class="container-fluid web-main-content">
      <div>
        <MaydayWebHome v-if="isHome" class="mw-home" />
        <MaydayWebNode
          v-else-if="isNode"
          :companyColor="companyColor"
          @open-feedback-modal="openFeedbackModal"
        />
      </div>
      <div v-if="displayScroll" class="scroll-to-top">
        <el-button @click="scrollToTop">{{
          $t('mayday-web.scroll-to-top')
        }}</el-button>
      </div>
      <MaydayWebFeedbackDrawer
        v-if="modals.feedback"
        :display="modals.feedback"
        :content="content"
        :content-parents="contentParents"
        :path="navPath"
        @close="modals.feedback = false"
        @success="handleSuccess"
      />
    </div>
  </div>
</template>

<script>
import MaydayWebSearchWrapper from '@/components/SuperSearch/MaydayWebSearchWrapper';
import MaydayWebHome from '@/views/MaydayWeb/MaydayWebHome';
import MaydayWebNode from '@/views/MaydayWeb/MaydayWebNode';
import MaydayWebUserAvatar from '@/layout/MaydayWeb/MaydayWebUserAvatar.vue';
import NotificationPopover from '@/components/Notifications/NotificationPopover.vue';
import UrgentNotificationsCard from '@/components/Notifications/UrgentNotificationsCard';
import MaydayWebUserFavorites from 'views/MaydayWeb/MaydayWebUserFavorites';
import TextEllipsis from '@/components/TextEllipsis';
import TranslationDropdown from '@/components/TranslationDropdown';

import { mapActions, mapGetters, mapState } from 'vuex';
import { fontColorAccordingToBackground } from 'utils/webKnowledgeModule';
import MaydayWebFeedbackDrawer from 'views/MaydayWeb/MaydayWebFeedbackDrawer.vue';

export default {
  name: 'MaydayWebLayout',
  components: {
    MaydayWebSearchWrapper,
    NotificationPopover,
    MaydayWebHome,
    MaydayWebNode,
    MaydayWebUserAvatar,
    UrgentNotificationsCard,
    MaydayWebUserFavorites,
    MaydayWebFeedbackDrawer,
    TextEllipsis,
    TranslationDropdown,
  },
  data() {
    return {
      modals: {
        feedback: false,
      },
      content: null,
      contentParents: [],
      displayScroll: false,
    };
  },
  created() {
    this.resolveLanguage(this.$route);
    this.initSearch();
    this.getUrgentNotifications();
    this.fetchUserFavorites();
    document.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroyed() {
    document.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    ...mapActions('webKnowledgeModule', [
      'maydayWebSearchAction',
      'initSearch',
      'setNavigationLanguage',
    ]),
    ...mapActions('notificationModule', ['getUrgentNotifications']),
    ...mapActions('userFavoritesModule', ['fetchUserFavorites']),
    changeLanguage(language) {
      this.$router.push({ params: { ...this.$route.params, lang: language } });
    },
    openFeedbackModal() {
      const { name } = this.$route;

      if (name.includes('knowledge')) {
        this.content = {
          id: null,
        };
      } else if (name.includes('step')) {
        this.content = {
          id: this.focusDiagItem.id,
          type: this.focusDiagItem.type,
          label: this.focusDiagItem.label,
        };

        this.contentParents = this.focusDiagItem.ancestorsContent.reduce(
          (acc, ancestor) => {
            if (ancestor.type === 'Diagnostic') return acc; // No need to add as it is already present in breadcrumb path

            acc.push({
              id: ancestor.id,
              label: ancestor.label,
              type: ancestor.type,
            });
            return acc;
          },
          [],
        );
        this.contentParents.push(this.content); // Push current step as last item
      } else {
        // Content or Case
        this.content = {
          id: this.focusNavItem.id,
          type: this.focusNavItem.type || 'Case',
          label: this.focusNavItem.label,
        };
        this.contentParents = [];
      }

      this.modals.feedback = true;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    handleScroll() {
      this.displayScroll = window.scrollY > 100;
    },
    handleSuccess() {
      this.modals.feedback = false;
      this.$message({
        message: this.$t('knowledge.modals.feedback.success'),
        type: 'success',
      });
    },
    resolveLanguage(route) {
      const { params, name, query } = route;
      const { lang } = params;

      if (lang) return;

      this.$router.push({
        name,
        query,
        params: { ...params, lang: this.focusKnowledgeLanguage },
      });
    },
    ...mapActions('userFavoritesModule', ['fetchUserFavorites']),
    ...mapActions('webKnowledgeModule', ['goToWebTranslatedEntity']),
  },
  computed: {
    expandHeader() {
      return !!this.urgentNotifications.length && this.isHome;
    },
    isHome() {
      return (
        !this.showSearch &&
        ['mayday-web', 'mayday-web-by-knowledge'].includes(this.$route.name)
      );
    },
    isNode() {
      return this.showNode || this.loading;
    },
    mainHeader() {
      return this.focusKnowledgeLabel
        ? this.focusKnowledgeLabel
        : this.$t('mayday-web.main-header');
    },
    ...mapGetters(['hasAgentRestrictions']),
    ...mapGetters('webKnowledgeModule', [
      'companyColor',
      'companyLogo',
      'focusKnowledgeValue',
      'focusKnowledgeRoots',
      'focusKnowledgeLabel',
      'focusKnowledgeLanguage',
      'focusKnowledgeLanguages',
      'focusKnowledgeIsMultilinguale',
      'navPath',
      'showNode',
      'loading',
      'showSearch',
    ]),
    ...mapGetters('notificationModule', ['urgentNotifications']),
    ...mapState('webKnowledgeModule', [
      'focusKnowledge',
      'focusNavItem',
      'focusDiagItem',
      'navigationLanguage',
    ]),
    companyDynamicStyle() {
      return {
        '--custom-color': this.companyColor,
        '--font-color': fontColorAccordingToBackground(this.companyColor),
      };
    },
  },
  watch: {
    $route(newRoute) {
      this.resolveLanguage(newRoute);
    },
  },
};
</script>

<style>
.mw-body {
  background-color: white !important;
}
</style>

<style lang="scss" scoped>
.web-main-content {
  margin-top: 12px;
}
.scroll-to-top {
  position: fixed;
  bottom: 16px;
  right: 16px;
}
@media (max-width: 768px) {
  .scroll-to-top {
    display: none;
  }
}

.fade-enter-active {
  transition: opacity 0.5s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.header-row {
  padding-top: 8px;
  width: calc(100% - 200px);
  height: 60px !important;
}

.help-center {
  height: 40px !important;
  max-width: calc(100% - 150px);
}

.mw-header-container {
  position: relative;
  height: 200px;
  background-color: var(--custom-color);
  h1 {
    color: var(--font-color);
  }
}

.mw-header-container.expand-header {
  height: 300px;
}

.mw-header {
  height: 76px;
}

.mw-home {
  margin-top: 58px;
}

.urgent-notifications-card {
  width: 70vw;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}

.mw-search-wrapper {
  position: absolute;
  bottom: 80px;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);

  &.is-home {
    bottom: -28px;
  }
}

@media (max-width: 991px) {
  .mobile-navbar {
    height: 54px !important;
    width: 100%;
  }

  .mx-header-logo-wrapper {
    height: 40px !important;
  }

  .mw-header-logo {
    height: 40px !important;
  }
}

.mw-search-node-content {
  top: 8px;
  position: absolute;
}

.mw-search-input {
  margin-left: 8px;
  width: 100%;
  border: none;
  color: $grey-2-mayday;
  font-size: 18px;
  &::placeholder {
    color: $grey-4-mayday;
    opacity: 1;
  }
  &:-ms-input-placeholder {
    color: $grey-4-mayday;
    font-size: 18px;
  }
  &::-ms-input-placeholder {
    color: $grey-4-mayday;
    font-size: 18px;
  }
}

.mw-search-bar {
  font-size: 18px;
  color: $grey-4-mayday;
  width: 100%;
}

.mx-header-logo-wrapper {
  max-width: 120px;
  height: 60px;
  margin-left: 8px;
  background: white;
  border-radius: 2px;
}

.mw-header-logo {
  max-width: 120px;
  max-height: 60px;
  cursor: pointer;
}
.mw-icon {
  cursor: pointer;
  font-size: 16px;
}

.grey-hover-blue {
  color: $grey-4-mayday;
  &:hover {
    color: $blue-mayday;
    cursor: pointer;
  }
}
</style>
<style lang="scss" scoped>
.mw-search-bar {
  :deep() {
    .el-input__inner {
      padding-left: 32px;
      height: 52px;
    }
  }
}

.home-search-input {
  :deep() .el-input__inner {
    box-shadow: 0px 5px 40px rgba(112, 144, 176, 0.2);
  }
}
</style>
